import { useState, Fragment } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'

import DateActionCard, {
  getMonthFromDate,
  getDayFromDate,
} from './DateActionCard'

import { HeroFormWrapper, HeroFormTitle } from '~partials/Hero'
import track from '~helpers/track'
import '~helpers/yup-corporate-email'
import MarketoForm from '~components/MarketoForm'

const EventBodyWrapper = ({ hideWrapper, ...rest }) =>
  hideWrapper ? <div {...rest} /> : <HeroFormWrapper {...rest} />

EventBodyWrapper.propTypes = {
  hideWrapper: PropTypes.bool,
}

const EventBody = ({
  hideWrapper,
  title,
  onDemand,
  fieldValues,
  variation,
  ...rest
}) => {
  let formId

  // eslint-disable-next-line default-case
  switch (variation) {
    case 'weekly-live-demo':
      formId = 1257
      break
    case 'bootcamp':
      formId = 1260
      break
    case 'webinar':
      formId = 1017
      break
  }

  return (
    <EventBodyWrapper hideWrapper={hideWrapper} {...rest}>
      <HeroFormTitle>{title}</HeroFormTitle>
      <MarketoForm
        id={formId}
        fieldValues={fieldValues}
        onSuccess={() => {
          track('Event registered', {
            label: `marketo-${formId}`,
          })
        }}
        thankYouMessage={
          onDemand
            ? 'Thank you for registering for this on-demand event. You will receive an email momentarily with a link to watch the session.'
            : '<strong>Success!</strong> We have received your information and we will share more details about this event with you shortly.'
        }
      />
    </EventBodyWrapper>
  )
}

EventBody.propTypes = {
  onDemand: PropTypes.bool,
  title: PropTypes.string,
  hideWrapper: PropTypes.bool,
  fieldValues: PropTypes.object.isRequired,
  variation: PropTypes.oneOf(['webinar', 'bootcamp', 'weekly-live-demo'])
    .isRequired,
}

const SelectSession = ({ sessions, setFieldValues }) => {
  const theme = useTheme()

  return (
    <div
      css={{
        display: 'grid',
        gap: theme.space.base,
      }}
    >
      {sessions.map((session) => {
        const date = new Date(session.date)

        return (
          <DateActionCard
            key={session.id}
            date={session.date}
            time={session.time}
            actionLabel="Register now"
            actionProps={{
              onClick: () => {
                setFieldValues((prevState) => {
                  return {
                    ...prevState,
                    sessionDate: `${getMonthFromDate(date)} ${getDayFromDate(
                      date
                    )}`,
                    sessionId: session.id,
                  }
                })
              },
            }}
          />
        )
      })}
    </div>
  )
}

SelectSession.propTypes = {
  sessions: PropTypes.array,
  setFieldValues: PropTypes.func.isRequired,
}

export const EventForm = ({
  onDemand,
  title,
  hideWrapper,
  sessions,
  variation,
  fieldValues = {},
}) => {
  const [currentFieldValues, setFieldValues] = useState(fieldValues)

  const isFormHidden = sessions?.length > 0 && !currentFieldValues.sessionDate

  return (
    <Fragment>
      {isFormHidden ? (
        <SelectSession setFieldValues={setFieldValues} sessions={sessions} />
      ) : (
        <EventBody
          hideWrapper={hideWrapper}
          onDemand={onDemand}
          title={title}
          fieldValues={currentFieldValues}
          variation={variation}
        />
      )}
    </Fragment>
  )
}

EventForm.propTypes = {
  onDemand: PropTypes.bool,
  sessions: PropTypes.array,
  title: PropTypes.string,
  hideWrapper: PropTypes.bool,
  fieldValues: PropTypes.object,
  variation: PropTypes.oneOf(['webinar', 'bootcamp', 'weekly-live-demo'])
    .isRequired,
}

export default EventForm
